import {
  ComponentProps,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { MenuSelect } from "../../menu-select";
import { DrawerSelectContext } from "../drawer-select.context";
import { DrawerSelectOptionType } from "../drawer-select.types";
import { DrawerSelectGroupContext } from "./drawer-select-group.context";

export type DrawerSelectGroupProps = ComponentProps<"div"> & {
  displayCheckbox?: boolean;
  selectedOnly?: boolean;
  hideSelectedOptions?: boolean;
  ignoreFilter?: boolean;
  label?: ReactNode;
  maxHeight?: string;
};

export function DrawerSelectGroup({
  hideSelectedOptions = false,
  displayCheckbox = true,
  selectedOnly = false,
  ignoreFilter = false,
  maxHeight = "auto",
  children,
  label,
  ...props
}: DrawerSelectGroupProps) {
  const [options, setOptions] = useState<DrawerSelectOptionType[]>([]);
  const { isOptionSelected, maxOptionsReached, query, searchLimit } =
    useContext(DrawerSelectContext);

  const isVisibleBase = useCallback(
    (option: DrawerSelectOptionType) => {
      if (!isOptionSelected(option) && selectedOnly) {
        return false;
      }

      if (isOptionSelected(option) && hideSelectedOptions) {
        return false;
      }

      if (ignoreFilter) {
        return true;
      }

      return option.text.toLowerCase().includes(query?.toLowerCase() ?? "");
    },
    [isOptionSelected, selectedOnly, hideSelectedOptions, ignoreFilter, query],
  );

  const disableCheckbox = useCallback(
    (option: DrawerSelectOptionType) => {
      return maxOptionsReached && !isOptionSelected(option);
    },
    [maxOptionsReached, isOptionSelected],
  );

  const displayedOptions = useMemo(() => {
    const visible = options.filter(isVisibleBase);
    const hasActiveSearch = (query ?? "").trim() !== "";
    if (hasActiveSearch && searchLimit != null) {
      return visible.slice(0, searchLimit);
    }
    return visible;
  }, [options, isVisibleBase, query, searchLimit]);

  const displayedSet = useMemo(
    () => new Set(displayedOptions.map((o) => o.value)),
    [displayedOptions],
  );

  const isVisible = useCallback(
    (option: DrawerSelectOptionType) => displayedSet.has(option.value),
    [displayedSet],
  );

  const pushOption = useCallback((option: DrawerSelectOptionType) => {
    setOptions((prev) => [...prev, option]);
  }, []);

  const removeOption = useCallback((option: DrawerSelectOptionType) => {
    setOptions((prev) => prev.filter((o) => o.value !== option.value));
  }, []);

  const contextValue = useMemo(
    () => ({
      pushOption,
      removeOption,
      displayCheckbox,
      isVisible,
      disableCheckbox,
    }),
    [pushOption, removeOption, displayCheckbox, isVisible, disableCheckbox],
  );

  const hasVisibleOptions = displayedOptions.length > 0;

  return (
    <DrawerSelectGroupContext.Provider value={contextValue}>
      <div {...props} style={{ display: hasVisibleOptions ? "block" : "none" }}>
        <MenuSelect>
          {label && <MenuSelect.Title>{label}</MenuSelect.Title>}
          <MenuSelect.Body maxHeight={maxHeight}>{children}</MenuSelect.Body>
        </MenuSelect>
      </div>
    </DrawerSelectGroupContext.Provider>
  );
}
